import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * Transcript chat composer (R14 B5 → R23 C1).
 *
 * Field learning (three sessions, dev-89): the open-on-demand choreography failed — visitors couldn't
 * find the box ("I don't see a box" / "can you open the type box"). R23 C1 (USER-SPECIFIED) makes the
 * composer **ALWAYS VISIBLE**, anchored at the BOTTOM OF THE CHAT TRANSCRIPT (below the transcript, NOT
 * above the control bar — chat convention, refining the R14 layout rule). It's a passive single-line
 * field at rest; it expands (2 rows) on focus so a longer turn is comfortable, and settles back when
 * empty + blurred. Typing is always a peer to voice (§4.2) — the noisy-hall / low-STT / accessibility
 * channel, now permanently on screen so it never has to be "opened".
 *
 * The Type control in the bar no longer opens/closes anything — it FOCUS/SCROLL-TO's this field (and
 * stays the §31 pulse target). The host drives that through the imperative `focus()` handle exposed
 * here, which also scrolls the composer into view so the on-screen keyboard (kiosk portrait) doesn't
 * occlude it. Presentation only, tokens + Icon primitive.
 */

export type ComposerHandle = {
  /** Focus the field and scroll it into view (used by the Type control + on §31 pulse-clear). */
  focus: () => void;
};

type Props = {
  /** Send the typed turn to Gigi (useVoice.sendUserText → §29.2 user-text path, unchanged). */
  onSend: (text: string) => void;
  /**
   * R27 — the "Speak naturally…" hint is folded into the composer placeholder (it floated loose as a
   * separate status line before). When the mic is live the placeholder invites speaking-or-typing;
   * when muted it points at typing as the channel. Presentation-only.
   */
  micEnabled?: boolean;
  /**
   * R30 V1 — emit the DEBOUNCED §40.4b `composer-draft` signal while the visitor types in the box (an
   * out-of-flow email/phone can be typed here, §29.2 / K2). The host coalesces keystrokes and emits
   * the settled draft; `onDraftStop` cancels it on empty/submit/blur. Presentation is UNCHANGED — this
   * only mirrors the in-progress text to the server so Gigi can confirm it without a re-ask.
   */
  onDraftChange?: (value: string) => void;
  onDraftStop?: () => void;
};

export const TranscriptComposer = forwardRef<ComposerHandle, Props>(function TranscriptComposer(
  { onSend, micEnabled = true, onDraftChange, onDraftStop },
  ref
) {
  const [text, setText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        // Scroll into view so the on-screen keyboard (kiosk portrait) doesn't hide the field.
        el.scrollIntoView({ block: 'end', behavior: 'smooth' });
      },
    }),
    []
  );

  function submit() {
    const t = text.trim();
    if (t === '') return;
    onSend(t);
    setText('');
    // R30 V1 — the turn is committed (sent as a §29.2 user-text); the draft signal is done for it.
    onDraftStop?.();
    // Keep the composer focused for a follow-up turn (chat convention).
    inputRef.current?.focus();
  }

  const hasText = text.trim() !== '';

  return (
    <div className="shrink-0 flex w-full items-end gap-2 pt-1" data-testid="transcript-composer">
      <textarea
        ref={inputRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          // R30 V1 — emit the debounced draft while typing; an empty value stops the signal.
          if (e.target.value.trim() === '') onDraftStop?.();
          else onDraftChange?.(e.target.value);
        }}
        onFocus={() => setExpanded(true)}
        onBlur={() => {
          // Collapse back to the passive single line only when empty (keep a drafted turn visible).
          if (text.trim() === '') setExpanded(false);
          // R30 V1 — stop the draft signal on blur (cancels the pending emit). A drafted-but-unblurred
          // turn keeps emitting; a blur means the visitor stepped away from the box.
          onDraftStop?.();
        }}
        onKeyDown={(e) => {
          // Enter sends (single-line convention); Shift+Enter inserts a newline in the expanded field.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        rows={expanded ? 2 : 1}
        placeholder={
          micEnabled ? 'Speak naturally, or type a message…' : 'Microphone muted — type a message…'
        }
        aria-label="Type a message to Gigi"
        // R29 X2.2 — the composer is a CARVED field (`.field-inset`): recessed into the panel (the
        // content-input tier), rounded to match its chat pill shape. Focus lifts a brass ring on the
        // carve (see .field-inset:focus-within). Tokens only.
        className="field-inset flex-1 resize-none rounded-2xl px-4 py-2.5 text-sm text-ink outline-none placeholder:text-faint"
      />
      {/* R29 X2.2 — Send is the BRASS PILL pressable (foil-gradient, lift→sink), matching the close
          card's send affordance so the commit action reads the same across the surface. */}
      <button
        type="button"
        onClick={submit}
        disabled={!hasText}
        aria-label="Send"
        className="pill-brass inline-flex shrink-0 items-center gap-1.5 px-4 py-2.5 text-sm"
      >
        <Icon name="send" size={16} />
        Send
      </button>
    </div>
  );
});
